import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, List, Plus, LayoutGrid, BarChart2, ShoppingBag, Car, IndianRupee, BookOpen, Coffee, CreditCard, ChevronLeft, Delete } from 'lucide-react';

interface PhoneMockupProps {
  view?: 'dashboard' | 'analytics'; // Controlled view for non-interactive mode
  interactive?: boolean; // Enable sandbox mode
}

type Transaction = {
  id: number;
  title: string;
  category: string;
  amount: number;
  date: string;
  type: 'expense' | 'income';
  icon: any;
  color: string;
};

const initialTransactions: Transaction[] = [
  { id: 1, title: 'Auto', category: 'Transportation', amount: 50, date: '4 Jan', type: 'expense', icon: Car, color: 'text-yellow-600' },
  { id: 2, title: 'Salary', category: 'Salary', amount: 10000, date: '4 Jan', type: 'income', icon: IndianRupee, color: 'text-green-500' },
  { id: 3, title: 'Kaffeine', category: 'Food & Dining', amount: 292, date: '4 Jan', type: 'expense', icon: Coffee, color: 'text-yellow-500' },
  { id: 4, title: 'Popcorn', category: 'Food & Dining', amount: 150, date: '1 Jan', type: 'expense', icon: ShoppingBag, color: 'text-red-400' },
];

const PhoneMockup: React.FC<PhoneMockupProps> = ({ view: controlledView = 'dashboard', interactive = false }) => {
  // --- Interactive State ---
  const [activeTab, setActiveTab] = useState<'dashboard' | 'transactions' | 'analytics'>('dashboard');
  const [showAddScreen, setShowAddScreen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>(initialTransactions);
  
  // Add Screen State
  const [newAmount, setNewAmount] = useState('0');
  const [selectedCategory, setSelectedCategory] = useState('Food');

  // Derived Data
  const totalSpending = transactions.filter(t => t.type === 'expense').reduce((acc, curr) => acc + curr.amount, 0);
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((acc, curr) => acc + curr.amount, 0);
  const balance = totalIncome - totalSpending;

  // Determine current view based on mode
  const currentView = interactive 
    ? (showAddScreen ? 'add' : activeTab) 
    : controlledView;

  // --- Handlers ---
  const handleNumPad = (num: string) => {
    if (newAmount === '0' && num !== '.') setNewAmount(num);
    else if (newAmount.includes('.') && num === '.') return;
    else if (newAmount.length < 7) setNewAmount(newAmount + num);
  };

  const handleDelete = () => {
    if (newAmount.length > 1) setNewAmount(newAmount.slice(0, -1));
    else setNewAmount('0');
  };

  const handleSave = () => {
    const amount = parseFloat(newAmount);
    if (amount > 0) {
      const newTxn: Transaction = {
        id: Date.now(),
        title: selectedCategory, // Simplified for demo
        category: selectedCategory,
        amount: amount,
        date: 'Just now',
        type: 'expense',
        icon: ShoppingBag, // Default icon
        color: 'text-white'
      };
      setTransactions([newTxn, ...transactions]);
      setNewAmount('0');
      setShowAddScreen(false);
      setActiveTab('dashboard');
    }
  };

  // Reset to controlled view if interactive prop changes
  useEffect(() => {
    if (!interactive) {
      setShowAddScreen(false);
    }
  }, [interactive]);

  return (
    <div className={`relative mx-auto border-zinc-900 bg-zinc-950 border-[10px] rounded-[2.5rem] h-[600px] w-[300px] shadow-2xl flex flex-col overflow-hidden ring-1 ring-zinc-800 select-none ${interactive ? 'cursor-default' : 'pointer-events-none'}`}>
      {/* Dynamic Island */}
      <div className="h-[28px] w-full bg-zinc-950 absolute top-0 left-0 z-20 flex justify-center pt-2">
        <div className="h-[18px] w-[80px] bg-black rounded-full"></div>
      </div>

      {/* Screen Content */}
      <div className={`flex-1 bg-[#121212] w-full overflow-hidden flex flex-col pt-8 relative ${interactive ? 'pointer-events-auto' : ''}`}>
        
        {/* Header - Hides on Add Screen */}
        <AnimatePresence>
          {currentView !== 'add' && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="px-4 pb-4 flex justify-between items-center z-10"
            >
              <div className="flex items-center gap-2">
                <div className="text-yellow-400">
                  <BookOpen size={20} strokeWidth={2.5} />
                </div>
                <h3 className="text-lg font-bold text-zinc-100">Hisab Kitab</h3>
              </div>
              <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs shadow-lg">C</div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Router */}
        <div className="relative flex-1 w-full overflow-hidden">
          <AnimatePresence mode="wait">
            
            {/* --- DASHBOARD VIEW --- */}
            {currentView === 'dashboard' && (
              <motion.div 
                key="dashboard"
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                className="absolute inset-0 overflow-y-auto px-4 no-scrollbar pb-24"
              >
                <h4 className="text-zinc-100 font-bold mb-3 text-lg">Overview of January</h4>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-[#1e1e24] p-3 rounded-xl border border-zinc-800/50">
                    <p className="text-zinc-400 text-xs mb-1">Spending</p>
                    <h3 className="text-xl font-bold text-zinc-100">₹{totalSpending.toLocaleString()}</h3>
                    <p className="text-red-500 text-xs font-medium mt-1">-10%</p>
                  </div>
                  <div className="bg-[#1e1e24] p-3 rounded-xl border border-zinc-800/50">
                    <p className="text-zinc-400 text-xs mb-1">Income</p>
                    <h3 className="text-xl font-bold text-zinc-100">₹{totalIncome.toLocaleString()}</h3>
                    <p className="text-green-500 text-xs font-medium mt-1">+15%</p>
                  </div>
                </div>

                {/* Balance Card */}
                <div className="bg-[#1e1e24] p-4 rounded-xl border border-zinc-800/50 mb-6 relative overflow-hidden group">
                   <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/5 rounded-full blur-xl"></div>
                   <p className="text-zinc-400 text-xs mb-1 relative z-10">Balance</p>
                   <h3 className="text-2xl font-bold text-zinc-100 relative z-10">₹{balance.toLocaleString()}</h3>
                   <p className="text-green-500 text-xs font-medium mt-1 relative z-10">+5%</p>
                </div>

                {/* Recent Transactions */}
                <div className="flex justify-between items-end mb-3">
                    <h4 className="font-bold text-zinc-100">Recent</h4>
                    <button 
                      onClick={() => interactive && setActiveTab('transactions')}
                      className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                    >
                        See All
                    </button>
                </div>

                <div className="bg-[#1e1e24] rounded-xl border border-zinc-800/50 overflow-hidden">
                  {transactions.slice(0, 4).map((item, idx) => (
                    <motion.div 
                      key={item.id} 
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * idx }}
                      className="p-3 flex items-center gap-3 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center bg-zinc-800 text-zinc-400`}>
                        <item.icon size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-zinc-200">{item.title}</p>
                        <p className="text-[10px] text-zinc-500">{item.category} • {item.date}</p>
                      </div>
                      <p className={`text-sm font-bold ${item.type === 'income' ? 'text-green-500' : 'text-zinc-200'}`}>
                        {item.type === 'expense' ? '-' : '+'}₹{item.amount}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* --- TRANSACTIONS LIST VIEW --- */}
            {currentView === 'transactions' && (
               <motion.div 
                 key="transactions"
                 initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                 className="absolute inset-0 overflow-y-auto px-4 no-scrollbar pb-24"
               >
                  <h4 className="text-zinc-100 font-bold mb-4 text-lg">All Transactions</h4>
                  <div className="space-y-2">
                     {transactions.map((item) => (
                        <div key={item.id} className="bg-[#1e1e24] p-3 rounded-xl border border-zinc-800/50 flex items-center gap-3">
                           <div className={`h-10 w-10 rounded-full flex items-center justify-center bg-zinc-800 text-zinc-400`}>
                              <item.icon size={16} />
                           </div>
                           <div className="flex-1">
                              <p className="text-sm font-semibold text-zinc-200">{item.title}</p>
                              <p className="text-[10px] text-zinc-500">{item.date}</p>
                           </div>
                           <p className={`text-sm font-bold ${item.type === 'income' ? 'text-green-500' : 'text-zinc-200'}`}>
                              {item.type === 'expense' ? '-' : '+'}₹{item.amount}
                           </p>
                        </div>
                     ))}
                  </div>
               </motion.div>
            )}

            {/* --- ANALYTICS VIEW --- */}
            {currentView === 'analytics' && (
              <motion.div 
                key="analytics"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.2 }}
                className="absolute inset-0 overflow-y-auto px-4 pb-24"
              >
                 <div className="flex justify-between items-center mb-6">
                    <button className="text-yellow-400 font-medium text-sm border-b-2 border-yellow-400 pb-1">Spending</button>
                    <button className="text-zinc-500 font-medium text-sm pb-1">Income</button>
                 </div>
                 <div className="mb-6 text-center">
                    <h2 className="text-3xl font-bold text-zinc-100">₹{totalSpending}</h2>
                    <p className="text-xs text-zinc-500">Total Spent in Jan</p>
                 </div>
                 <div className="h-40 w-full flex items-end gap-4 mb-8 border-b border-zinc-800 pb-4">
                     <div className="flex-1 flex flex-col justify-end gap-2 h-full"><div className="w-full bg-yellow-500 rounded-t-lg h-[85%]"></div></div>
                     <div className="flex-1 flex flex-col justify-end gap-2 h-full"><div className="w-full bg-yellow-600 rounded-t-lg h-[45%]"></div></div>
                     <div className="flex-1 flex flex-col justify-end gap-2 h-full"><div className="w-full bg-yellow-700 rounded-t-lg h-[60%]"></div></div>
                 </div>
                 <div className="space-y-3">
                    <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-yellow-500"></div><p className="text-sm text-zinc-300 flex-1">Food</p><p className="text-sm font-bold">45%</p></div>
                    <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full bg-yellow-600"></div><p className="text-sm text-zinc-300 flex-1">Travel</p><p className="text-sm font-bold">30%</p></div>
                 </div>
              </motion.div>
            )}

            {/* --- ADD EXPENSE SCREEN (Interactive Only) --- */}
            {currentView === 'add' && (
               <motion.div
                  key="add"
                  initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                  className="absolute inset-0 bg-[#121212] z-50 flex flex-col"
               >
                  {/* Top Bar */}
                  <div className="px-4 py-4 flex items-center">
                     <button onClick={() => setShowAddScreen(false)} className="p-2 -ml-2 text-zinc-400 hover:text-white"><ChevronLeft /></button>
                     <span className="ml-2 font-bold text-zinc-100">Add Expense</span>
                  </div>

                  {/* Amount Input Display */}
                  <div className="px-6 py-8 text-center">
                     <p className="text-zinc-500 text-sm mb-2">Amount</p>
                     <div className="flex items-center justify-center text-5xl font-bold text-white">
                        <span className="text-2xl mr-1 text-zinc-500">₹</span>
                        {newAmount}
                     </div>
                  </div>

                  {/* Category Chips */}
                  <div className="px-4 mb-6">
                     <p className="text-xs text-zinc-500 mb-3 px-2">Category</p>
                     <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 px-2">
                        {['Food', 'Travel', 'Bills', 'Shopping', 'Health'].map(cat => (
                           <button 
                              key={cat}
                              onClick={() => setSelectedCategory(cat)}
                              className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-yellow-400 text-black' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}
                           >
                              {cat}
                           </button>
                        ))}
                     </div>
                  </div>

                  {/* Numpad */}
                  <div className="flex-1 bg-zinc-900 rounded-t-3xl p-6 grid grid-cols-3 gap-4">
                     {[1,2,3,4,5,6,7,8,9, '.', 0].map(num => (
                        <button 
                           key={num} 
                           onClick={() => handleNumPad(num.toString())}
                           className="text-2xl font-semibold text-zinc-300 hover:bg-zinc-800 rounded-xl transition-colors py-2"
                        >
                           {num}
                        </button>
                     ))}
                     <button onClick={handleDelete} className="flex items-center justify-center text-zinc-400 hover:text-red-400 transition-colors"><Delete size={24} /></button>
                  </div>

                  {/* Save Button */}
                  <div className="p-4 bg-zinc-900">
                     <button 
                        onClick={handleSave}
                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 rounded-xl shadow-lg shadow-yellow-400/20 active:scale-95 transition-all"
                     >
                        Save Transaction
                     </button>
                  </div>
               </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* --- GLOBAL ELEMENTS (FAB & NAV) --- */}
        
        {/* FAB: Only show on main tabs */}
        {!showAddScreen && (
            <motion.div 
               className="absolute bottom-20 right-5 z-20"
               initial={{ scale: 0 }} animate={{ scale: 1 }}
               whileHover={interactive ? { scale: 1.1 } : {}}
               whileTap={interactive ? { scale: 0.9 } : {}}
            >
               <button 
                  onClick={() => interactive && setShowAddScreen(true)}
                  className={`h-14 w-14 bg-yellow-400 rounded-2xl shadow-lg shadow-black/50 flex items-center justify-center text-zinc-900 transition-colors ${interactive ? 'cursor-pointer hover:bg-yellow-300' : ''}`}
               >
                  <Plus size={28} />
               </button>
            </motion.div>
        )}

        {/* Bottom Nav */}
        <div className="absolute bottom-0 w-full bg-[#121212] border-t border-zinc-800 h-16 flex justify-around items-center pb-2 z-20">
            <button 
               onClick={() => interactive && setActiveTab('dashboard')}
               className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'dashboard' && !showAddScreen ? 'text-yellow-400' : 'text-zinc-600'} ${interactive ? 'hover:text-zinc-400' : ''}`}
            >
              <Home size={22} strokeWidth={activeTab === 'dashboard' ? 3 : 2} />
              <span className="text-[10px] font-medium">Dashboard</span>
            </button>
            <button 
               onClick={() => interactive && setActiveTab('transactions')}
               className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'transactions' && !showAddScreen ? 'text-yellow-400' : 'text-zinc-600'} ${interactive ? 'hover:text-zinc-400' : ''}`}
            >
              <List size={22} strokeWidth={activeTab === 'transactions' ? 3 : 2} />
              <span className="text-[10px] font-medium">Transac..</span>
            </button>
            <button 
               onClick={() => interactive && setActiveTab('analytics')}
               className={`flex flex-col items-center gap-1 transition-colors ${activeTab === 'analytics' && !showAddScreen ? 'text-yellow-400' : 'text-zinc-600'} ${interactive ? 'hover:text-zinc-400' : ''}`}
            >
               <BarChart2 size={22} strokeWidth={activeTab === 'analytics' ? 3 : 2} />
               <span className="text-[10px] font-medium">Insights</span>
            </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneMockup;